import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import * as THREE from 'three';

const MAX_LABELS = 60;
const REFRESH_FRAMES = 12;
const _cam = new THREE.Vector3();
const _pos = new THREE.Vector3();

export default function HoleCollarLabels({ holes, visible }) {
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);
  const frameRef = useRef(0);
  const [shown, setShown] = useState([]);

  const collars = useMemo(() => {
    const out = [];
    Object.keys(holes || {}).forEach((id) => {
      const hole = holes[id];
      const p = hole?.points?.[0];
      if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) return;
      const label = String(hole?.data?.Hole_ID || hole?.holeId || id);
      if (!label) return;
      out.push({ id, label, x: p.x, y: p.y, z: p.z });
    });
    return out;
  }, [holes]);

  useFrame(() => {
    if (!visible || !collars.length) return;
    frameRef.current += 1;
    if (frameRef.current % REFRESH_FRAMES !== 0) return;
    camera.getWorldPosition(_cam);
    const ranked = collars
      .map((row) => {
        _pos.set(row.x, row.y, row.z);
        return { ...row, d2: _cam.distanceToSquared(_pos) };
      })
      .sort((a, b) => a.d2 - b.d2)
      .slice(0, MAX_LABELS);
    setShown((prev) => {
      if (
        prev.length === ranked.length &&
        prev.every((row, i) => row.id === ranked[i].id)
      ) {
        return prev;
      }
      return ranked;
    });
    invalidate();
  });

  useEffect(() => {
    if (!visible) setShown([]);
  }, [visible]);

  if (!visible || !shown.length) return null;

  return (
    <group raycast={() => false}>
      {shown.map((row) => (
        <Billboard
          key={row.id}
          position={[row.x, row.y, row.z]}
          follow
          lockX={false}
          lockY={false}
          lockZ={false}
          raycast={() => false}
        >
          <Text
            fontSize={8}
            color="#f4f4f5"
            outlineWidth={0.4}
            outlineColor="#09090b"
            anchorX="left"
            anchorY="middle"
            position={[4, 4, 0]}
            raycast={() => false}
            userData={{ mdvSkipPick: true }}
          >
            {row.label}
          </Text>
        </Billboard>
      ))}
    </group>
  );
}
